/******************************************
*  Author : Riccardo Di Dato
*  Created On : Mon May 30 2022
*  File : AggregateStatsCollector.ts
*******************************************/

import { AuthProviderIdentifier } from '../login/AuthProviderIdentifier.js';
import { LoginMode } from "../login/LoginMode.js";
import { MembershipAnalyticsInfo, StatsCollector } from "./StatsCollector.js";
import { CTAStatsDetails, SOURCE_ELEMENT } from "./CTATypes.js";
import { TopicAction, DeviceAction, StatDeviceType, BookmarkAction, NewlsetterAction } from './UserActionTypes.js';
import { UserGender } from '@api-commons/api/types/UserDataTypes.js';
import { Logger } from '@ciaopeople/cp-shatsu/Logging';

/**
 * Classe che aggrega più stats collector
 */
export class AggregateStatsCollector implements StatsCollector {
    
    constructor(
        private collectors: StatsCollector[],
        private logger: Logger
    ) {

    }


    private allCatch(promise: Promise<void>): Promise<void> {
        return promise.catch( e => {
            this.logger.warn("A stats collector failed", e);
            return Promise.resolve();
        });
    }


    markUserPersInfo(age: number, userGender: UserGender, memberInfo?: MembershipAnalyticsInfo): Promise<void> {
        return Promise.all(this.collectors.map(
            coll => this.allCatch(coll.markUserPersInfo(age, userGender, memberInfo))
        )).then( );
    }

    pushCollector(coll: StatsCollector) {
        this.collectors.push(coll);
    }

    markLoginStatus(loggedIn: boolean, provider: AuthProviderIdentifier | "UNK"): Promise<void> {
        return Promise.all(this.collectors.map(
            coll =>  this.allCatch(coll.markLoginStatus(loggedIn, provider))
        )).then( );
    }

    loginDone(provider: AuthProviderIdentifier| "UNK", mode: LoginMode, newUser: boolean): Promise<void> {
        return Promise.all(this.collectors.map(
            coll =>  this.allCatch(coll.loginDone(provider, mode, newUser))
        )).then( );
    }

    logoutDone(provider: AuthProviderIdentifier| "UNK"): Promise<void> {
        return Promise.all(this.collectors.map(
            coll =>  this.allCatch(coll.logoutDone(provider))
        )).then( );
    }

    collectCTAData(details: CTAStatsDetails): Promise<void> {
        return Promise.all(this.collectors.map(
            coll =>  this.allCatch(coll.collectCTAData(details))
        )).then( );
    }


    collectTopicActionData(source_element: SOURCE_ELEMENT, name: TopicAction | NewlsetterAction, url: string): Promise<void> {
        return Promise.all(this.collectors.map(
            coll =>  this.allCatch(coll.collectTopicActionData(source_element, name, url))
        )).then( );
    }
    collectDeviceActionData(name: DeviceAction, device: StatDeviceType): Promise<void> {
        return Promise.all(this.collectors.map(
            coll =>  this.allCatch(coll.collectDeviceActionData(name, device))
        )).then( );
    }

    collectBookmarkActionData(source_element: SOURCE_ELEMENT, name: BookmarkAction, url: string): Promise<void> {
        return Promise.all(this.collectors.map(
            coll =>  this.allCatch(coll.collectBookmarkActionData(source_element, name, url))
        )).then( );
    }

    collectPageview(page: string): Promise<void> {
        return Promise.all(this.collectors.map(
            coll =>  this.allCatch(coll.collectPageview(page))
        )).then( );
    }

    iosNotificationOpen(): Promise<void> {
        return Promise.all(this.collectors.map(
            coll =>  this.allCatch(coll.iosNotificationOpen())
        )).then( );
    }

    collectBuyMembershipButtonClick(loginRequired: boolean): Promise<void> {
        return Promise.all(this.collectors.map(
            coll =>  this.allCatch(coll.collectBuyMembershipButtonClick(loginRequired))
        )).then( );
    }

    collectLoginDialogButtonPress(sourceSection: AuthProviderIdentifier ): Promise<void> {
        return Promise.all(this.collectors.map(
            coll =>  this.allCatch(coll.collectLoginDialogButtonPress(sourceSection))
        )).then( );
    }

}